import { useState, useRef, useEffect } from 'react';
import {
  FaSatelliteDish,
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaRightToBracket,
  FaShieldHalved,
  FaChartLine,
  FaHeadset,
} from 'react-icons/fa6';
import { AuthService } from '../../services/AuthService';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

/* ============================================
   HOOK: Network Canvas untuk background login
   ============================================ */
function useNetworkCanvas(canvasRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let mouse = { x: -9999, y: -9999 };

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const count = Math.min(60, Math.floor((canvas.width * canvas.height) / 20000));
    const nodes = Array.from({ length: count }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      r: Math.random() * 1.8 + 0.6,
      o: Math.random() * 0.35 + 0.1,
    }));

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
    };
    const onLeave = () => {
      mouse.x = -9999;
      mouse.y = -9999;
    };
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);

    const maxD = 130;
    const mouseD = 180;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
        const dm = Math.hypot(mouse.x - n.x, mouse.y - n.y);
        let op = n.o;
        let rad = n.r;
        if (dm < mouseD) {
          op = Math.min(1, op + (1 - dm / mouseD) * 0.5);
          rad += (1 - dm / mouseD) * 2;
        }
        ctx.beginPath();
        ctx.arc(n.x, n.y, rad, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,180,216,${op})`;
        ctx.fill();
      }
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const d = Math.hypot(nodes[i].x - nodes[j].x, nodes[i].y - nodes[j].y);
          if (d < maxD) {
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(0,180,216,${(1 - d / maxD) * 0.1})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
        const dm = Math.hypot(mouse.x - nodes[i].x, mouse.y - nodes[i].y);
        if (dm < mouseD) {
          ctx.beginPath();
          ctx.moveTo(nodes[i].x, nodes[i].y);
          ctx.lineTo(mouse.x, mouse.y);
          ctx.strokeStyle = `rgba(72,202,228,${(1 - dm / mouseD) * 0.2})`;
          ctx.lineWidth = 0.6;
          ctx.stroke();
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
    };
  }, [canvasRef]);
}

/* ============================================
   KOMPONEN: Login
   ============================================ */
export const Login = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  useNetworkCanvas(canvasRef);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Trigger mount animation
  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email wajib diisi';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format email tidak valid';
    }
    if (!formData.password) {
      newErrors.password = 'Password wajib diisi';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password minimal 6 karakter';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const response = await AuthService.login({ data: formData });
      localStorage.setItem('token-radiustrend', response?.data?.token);
      localStorage.setItem('token_type-radiustrend', response?.data?.token_type);
      localStorage.setItem('user_data-radiustrend', JSON.stringify(response?.data?.user));
      navigate('/dashboard-menu');
    } catch (error) {
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const inputCls = (field) =>
    `w-full pl-12 pr-4 py-3.5 text-sm rounded-xl transition-all duration-300 outline-none font-body ${
      errors[field]
        ? 'bg-red-500/8 border border-red-500/40 text-white placeholder-red-300/40 focus:border-red-400 focus:ring-1 focus:ring-red-500/30'
        : 'bg-navy-700/50 border border-navy-500/40 text-white placeholder-slate-custom/40 focus:border-ocean-600/60 focus:ring-1 focus:ring-ocean-600/30'
    }`;

  const brandingFeatures = [
    { icon: FaShieldHalved, text: 'Secure & Reliable' },
    { icon: FaChartLine, text: 'Real-time Analytics' },
    { icon: FaHeadset, text: '24/7 Support' },
  ];

  return (
    <>
      <Helmet>
        <title>Login | Radius Trend - Billing Management System</title>
        <meta
          name="description"
          content="Masuk ke akun Radius Trend Anda untuk mengelola billing ISP dan manajemen jaringan secara otomatis."
        />
      </Helmet>

      <main className="relative min-h-screen flex items-center justify-center bg-navy-900 p-4 sm:p-6 overflow-hidden">
        {/* ===== Network Canvas Background ===== */}
        <canvas
          ref={canvasRef}
          className="absolute top-0 left-0 w-full h-full z-0"
          aria-hidden="true"
        />

        {/* ===== Gradient Orbs ===== */}
        <div
          className="absolute w-[500px] h-[500px] bg-ocean-900/30 -top-[20%] -left-[10%] rounded-full"
          style={{ filter: 'blur(100px)', animation: 'float 8s ease-in-out infinite' }}
          aria-hidden="true"
        />
        <div
          className="absolute w-[400px] h-[400px] bg-ocean-800/20 -bottom-[15%] -right-[8%] rounded-full"
          style={{ filter: 'blur(100px)', animation: 'floatSlow 10s ease-in-out infinite' }}
          aria-hidden="true"
        />
        <div
          className="absolute w-[250px] h-[250px] bg-navy-500/20 top-[40%] right-[20%] rounded-full"
          style={{ filter: 'blur(80px)', animation: 'float 12s ease-in-out 3s infinite' }}
          aria-hidden="true"
        />

        {/* ===== Login Card ===== */}
        <article
          className={`
            relative z-10 grid grid-cols-1 lg:grid-cols-2 max-w-5xl w-full
            rounded-3xl overflow-hidden min-h-[580px]
            transition-all duration-1000 cubic-bezier(0.16, 1, 0.3, 1)
            ${mounted ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-[0.97]'}
          `}
          style={{
            background: 'rgba(11, 20, 38, 0.65)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            border: '1px solid rgba(0, 180, 216, 0.1)',
            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.5), 0 0 80px rgba(0, 180, 216, 0.06)',
          }}
        >
          {/* ===== Left Panel — Branding ===== */}
          <section
            className="hidden lg:flex flex-col justify-between relative overflow-hidden p-12 lg:p-14"
            style={{
              background: 'linear-gradient(160deg, rgba(0, 119, 182, 0.25) 0%, rgba(0, 180, 216, 0.08) 50%, rgba(6, 13, 31, 0.4) 100%)',
              borderRight: '1px solid rgba(0, 180, 216, 0.08)',
            }}
            aria-label="Informasi Radius Trend"
          >
            {/* Decorative circle */}
            <div
              className="absolute -bottom-1/3 -left-1/3 w-[120%] h-[120%] rounded-full opacity-[0.03]"
              style={{
                background: 'radial-gradient(circle, #00B4D8 0%, transparent 70%)',
                animation: 'floatSlow 20s ease-in-out infinite',
              }}
              aria-hidden="true"
            />

            <div className="relative z-10">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center mb-8"
                style={{
                  background: 'linear-gradient(135deg, rgba(0, 180, 216, 0.2), rgba(0, 119, 182, 0.1))',
                  border: '1px solid rgba(0, 180, 216, 0.2)',
                  boxShadow: '0 8px 24px rgba(0, 180, 216, 0.15)',
                }}
              >
                <FaSatelliteDish className="text-2xl text-ocean-500" aria-hidden="true" />
              </div>

              {/* H1: Brand name untuk SEO */}
              <h1 className="font-display text-4xl font-bold text-white mb-4 tracking-tight">
                Radius<span className="text-ocean-500">Trend</span>
              </h1>
              <p className="text-slate-custom text-[15px] leading-relaxed max-w-sm">
                Solusi manajemen billing ISP dan RT/RW Net yang efisien, aman, dan otomatis.
              </p>
            </div>

            <div className="relative z-10 flex flex-col gap-4 mt-8">
              {brandingFeatures.map((item, i) => {
                const Icon = item.icon
                return (
                  <div
                    key={i}
                    className="flex items-center gap-4"
                    style={{
                      animation: `slideUp 0.6s ease-out ${0.4 + i * 0.12}s both`,
                    }}
                  >
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{
                        background: 'rgba(0, 180, 216, 0.12)',
                        border: '1px solid rgba(0, 180, 216, 0.2)',
                      }}
                    >
                      <Icon className="text-ocean-500 text-sm" aria-hidden="true" />
                    </div>
                    <span className="text-slate-custom text-sm font-medium">
                      {item.text}
                    </span>
                  </div>
                )
              })}
            </div>

            {/* Bottom decorative line */}
            <div
              className="absolute bottom-0 left-8 right-8 h-px"
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(0, 180, 216, 0.2), transparent)',
              }}
              aria-hidden="true"
            />
          </section>

          {/* ===== Right Panel — Login Form ===== */}
          <section
            className="p-8 sm:p-12 lg:p-14 flex items-center justify-center"
            style={{
              animation: `fadeIn 0.8s ease-out 0.2s both`,
            }}
          >
            <div className="w-full max-w-md">
              {/* Header */}
              <header className="mb-10">
                {/* Logo mobile */}
                <div className="lg:hidden flex items-center gap-3 mb-8">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(0, 119, 182, 0.3), rgba(0, 180, 216, 0.15))',
                      border: '1px solid rgba(0, 180, 216, 0.2)',
                    }}
                  >
                    <FaSatelliteDish className="text-ocean-500 text-lg" aria-hidden="true" />
                  </div>
                  <span className="font-display font-bold text-xl text-white">
                    Radius<span className="text-ocean-500">Trend</span>
                  </span>
                </div>

                <h2 className="font-display text-3xl font-bold text-white mb-2 tracking-tight">
                  Selamat Datang
                </h2>
                <p className="text-sm text-slate-custom">
                  Silakan masuk untuk mengakses dashboard Anda
                </p>
              </header>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                {/* Email */}
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold text-slate-custom mb-2"
                  >
                    Alamat Email
                  </label>
                  <div className="relative">
                    <FaEnvelope
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-custom/50 text-base z-10"
                      aria-hidden="true"
                    />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="contoh@email.com"
                      className={inputCls('email')}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-xs text-red-400 font-medium mt-1.5 flex items-center gap-1">
                      <span className="inline-block w-1 h-1 rounded-full bg-red-400" aria-hidden="true" />
                      {errors.email}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-semibold text-slate-custom mb-2"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <FaLock
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-custom/50 text-base z-10"
                      aria-hidden="true"
                    />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      autoComplete="current-password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Minimal 6 karakter"
                      className={`${inputCls('password')} pr-12`}
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-custom/50 hover:text-ocean-500 transition-colors p-1"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                    >
                      {showPassword ? (
                        <FaEyeSlash className="text-base" />
                      ) : (
                        <FaEye className="text-base" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-xs text-red-400 font-medium mt-1.5 flex items-center gap-1">
                      <span className="inline-block w-1 h-1 rounded-full bg-red-400" aria-hidden="true" />
                      {errors.password}
                    </p>
                  )}
                </div>

                {/* Actions Row */}
                <div className="flex items-center justify-between text-sm pt-1">
                  <label className="flex items-center gap-2.5 text-slate-custom cursor-pointer select-none group">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-navy-500/40 bg-navy-700/50 text-ocean-600 focus:ring-ocean-600/30 focus:ring-offset-0 cursor-pointer transition"
                    />
                    <span className="group-hover:text-white transition-colors">Ingat saya</span>
                  </label>
                  <Link
                    to={'/forgot-password'}
                    className="text-ocean-500 font-semibold hover:text-ocean-400 transition-colors"
                  >
                    Lupa password?
                  </Link>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full justify-center text-base py-4 mt-2"
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>Memproses...</span>
                    </>
                  ) : (
                    <>
                      <span>Masuk Sekarang</span>
                      <FaRightToBracket className="text-base" />
                    </>
                  )}
                </button>
              </form>

              {/* Footer */}
              <footer className="mt-8 text-center text-sm text-slate-custom">
                <p>
                  Belum punya akun?{' '}
                  {/* <Link to={'/register'} */}
                  <Link to={'/coming-soon'}
                    className="text-ocean-500 font-semibold hover:text-ocean-400 transition-colors"
                  >
                    Daftar Disini
                  </Link>
                </p>
              </footer>
            </div>
          </section>
        </article>

        {/* ===== Bottom Copyright ===== */}
        <p
          className="absolute bottom-4 left-0 right-0 text-center text-xs text-slate-custom/30 z-10"
          style={{ animation: 'fadeIn 1s ease-out 1s both' }}
        >
          &copy; {new Date().getFullYear()} Radius Trend. Seluruh hak cipta dilindungi.
        </p>
      </main>

      {/* ===== Inline Keyframes (hanya untuk halaman ini) ===== */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        @keyframes floatSlow {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(3deg); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .font-display { font-family: 'Space Grotesk', sans-serif; }
        .font-body { font-family: 'Plus Jakarta Sans', sans-serif; }
        body { font-family: 'Plus Jakarta Sans', sans-serif; }

        /* Warna utilitas yang belum ada di Tailwind config global */
        .bg-navy-900 { background-color: #060D1F; }
        .bg-navy-700\/50 { background-color: rgba(15, 26, 46, 0.5); }
        .border-navy-500\/40 { border-color: rgba(26, 45, 74, 0.4); }
        .text-slate-custom { color: #8899AA; }
        .text-slate-custom\/40 { color: rgba(136, 153, 170, 0.4); }
        .text-slate-custom\/50 { color: rgba(136, 153, 170, 0.5); }
        .text-slate-custom\/30 { color: rgba(136, 153, 170, 0.3); }
        .text-ocean-500 { color: #48CAE4; }
        .text-ocean-400 { color: #90E0EF; }
        .text-ocean-600 { color: #00B4D8; }
        .text-red-400 { color: #f87171; }
        .text-red-300\/40 { color: rgba(252, 165, 165, 0.4); }
        .border-ocean-600\/60 { border-color: rgba(0, 180, 216, 0.6); }
        .border-ocean-600\/30 { border-color: rgba(0, 180, 216, 0.3); }
        .border-red-500\/40 { border-color: rgba(239, 68, 68, 0.4); }
        .border-red-400 { border-color: rgba(248, 113, 113, 1); }
        .ring-ocean-600\/30 { --tw-ring-color: rgba(0, 180, 216, 0.3); }
        .ring-red-500\/30 { --tw-ring-color: rgba(239, 68, 68, 0.3); }
        .bg-red-500\/8 { background-color: rgba(239, 68, 68, 0.08); }
        .focus\:border-ocean-600\/60:focus { border-color: rgba(0, 180, 216, 0.6); }
        .focus\:border-red-400:focus { border-color: rgba(248, 113, 113, 1); }
        .focus\:ring-ocean-600\/30:focus { --tw-ring-color: rgba(0, 180, 216, 0.3); }
        .focus\:ring-red-500\/30:focus { --tw-ring-color: rgba(239, 68, 68, 0.3); }

        /* Button classes yang sudah ada di index.css, fallback jika halaman ini diakses terpisah */
        .btn-primary {
          background: linear-gradient(135deg, #0077B6, #00B4D8);
          color: #fff;
          padding: 14px 32px;
          border-radius: 12px;
          font-weight: 600;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
          border: none;
          cursor: pointer;
          font-size: inherit;
          font-family: inherit;
        }
        .btn-primary::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent);
          transition: left 0.6s ease;
        }
        .btn-primary:hover::before { left: 100%; }
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 35px rgba(0, 180, 216, 0.4);
        }
        .btn-primary:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none !important;
          box-shadow: none !important;
        }

        /* Custom checkbox styling */
        input[type="checkbox"] {
          accent-color: #00B4D8;
        }
      `}</style>
    </>
  );
};

